import { useCallback } from 'react';
import { useRouter } from 'next/router';

import AnalyticsService, {
  AnalyticsEnum,
} from '@picsart/rc/services/analytics';
import useMount from '@picsart/rc/useMount';

import { SendEvent, PageOpen, UseAnalytics } from './types';

let initialized = false;
let queue = [];
const { NEXT_PUBLIC_ANALYTICS_DELAY } = process.env;
const useAnalytics: UseAnalytics = (gtmDelay = NEXT_PUBLIC_ANALYTICS_DELAY) => {
  const { route } = useRouter();
  const sendEvent: SendEvent = useCallback((eventAction, data) => {
    if (initialized) {
      AnalyticsService.sendEvent(eventAction, data);
    } else {
      queue.push(() => AnalyticsService.sendEvent(eventAction, data));
    }
  }, []);

  const initA = useCallback(() => {
    if (!initialized && route !== '/gtm-disabled') {
      initialized = true;
      AnalyticsService.init(
        'GTM-PQ45W6W',
        'blog',
        'dataLayer',
        AnalyticsEnum.gtag,
        Number(gtmDelay),
      );
      queue.forEach(event => event());
      queue = [];
    }
  }, []);

  // eslint-disable-next-line consistent-return
  useMount(() => {
    if (document.readyState === 'complete') {
      initA();
    } else {
      window.addEventListener('load', initA);
      return () => window.removeEventListener('load', initA);
    }
  });

  const sendEventNonInteraction: SendEvent = useCallback(
    (eventAction: string, data: any) => {
      if (initialized) {
        AnalyticsService.sendEventNonInteraction(eventAction, data);
      } else {
        queue.push(() =>
          AnalyticsService.sendEventNonInteraction(eventAction, data),
        );
      }
    },
    [],
  );

  const pushDataLayer: SendEvent = useCallback(
    (event: string, data: any) =>
      AnalyticsService.pushDataLayer({ event, data }),
    [],
  );

  const pageOpen: PageOpen = useCallback(
    options => {
      sendEventNonInteraction('page_open', options);
    },
    [sendEventNonInteraction],
  );

  const fireExperiment = (id, variant) => {
    sendEvent('experiment_variant_trigger', {
      experimentVersion: `${id}.${variant}`,
    });
  };

  return {
    pageOpen,
    sendEvent,
    pushDataLayer,
    fireExperiment,
  };
};

export default useAnalytics;
